import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, Box, Checkbox, Typography, Button, IconButton } from "@material-ui/core";
import { useSubscriptionStyles } from "./style";
import { Close } from "@material-ui/icons";
import { ReactComponent as IconCheckbox } from "../../../../assets/images/icons/iconCheckbox.svg"
import { ReactComponent as IconCheckFill } from "../../../../assets/images/icons/iconCheckFill.svg"
import { Plus } from "lucide-react";
import { clsx } from 'clsx';
import { showToast } from "../../../../utils/toast";
import { Link } from "react-router-dom";


type WelcomeUploadDialogProps = {
    open: boolean;
    setOpen: (isOpen: boolean) => void;
    uploadFiles: (files: File[], remainingMediaUrls: string[]) => void;
    initialMediaUrls?: string[];
}
export default function WelcomeUploadDialog({
    open,
    setOpen,
    uploadFiles,
    initialMediaUrls = [],
}: WelcomeUploadDialogProps) {
    const classes = useSubscriptionStyles();
    const [files, setFiles] = useState<File[]>([]);
    const [remoteMediaUrls, setRemoteMediaUrls] = useState<string[]>(initialMediaUrls);
    const [isAgreementChecked, setIsAgreementChecked] = useState(false);
    const limit = 3

    useEffect(() => {
        if (!open) return;
        setFiles([]);
        setRemoteMediaUrls(initialMediaUrls);
        setIsAgreementChecked(false);
    }, [initialMediaUrls, open]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (files.length + remoteMediaUrls.length + selectedFiles.length <= limit) {
            setFiles([...files, ...selectedFiles]);
        } else {
            showToast(`Maximum ${limit} files`);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (files.length + remoteMediaUrls.length + droppedFiles.length <= limit) {
            setFiles([...files, ...droppedFiles]);
        } else {
            showToast(`Maximum ${limit} files`);
        }
    }
    const clearFiles = () => {
        setFiles([])
        setRemoteMediaUrls([])
    }
    const handleSave = () => {
        uploadFiles(files, remoteMediaUrls)
        setOpen(false)
    }
    const previewItems = [
        ...remoteMediaUrls.map((url) => ({ type: "remote" as const, url })),
        ...files.map((file) => ({ type: "local" as const, file })),
    ];

    return (
        <Box position="relative">
            <Dialog hideBackdrop open={open} onClose={() => setOpen(false)} classes={{ root: classes.dialogRoot }} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <div>Attach your media</div>
                    <Close onClick={() => setOpen(false)} />
                </DialogTitle>
                <DialogContent >
                    {previewItems.length > 0 ?
                        <Box>
                            <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                {previewItems.map((item, idx) => {
                                    const isLocal = item.type === "local";
                                    const mediaSrc = isLocal ? URL.createObjectURL(item.file) : item.url;
                                    const isImage = isLocal
                                        ? item.file.type.startsWith("image/")
                                        : !/\.(mp4|webm|ogg)(\?|$)/i.test(item.url);
                                    return (
                                        <Box
                                            key={isLocal ? `local-${idx}` : `remote-${idx}`}
                                            position="relative"
                                            style={{ width: 108, height: 126 }}
                                        >
                                            {isImage ? (
                                                <img
                                                    src={mediaSrc}
                                                    alt={isLocal ? item.file.name : "welcome media"}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        borderRadius: 6,
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            ) : (
                                                <video
                                                    src={mediaSrc}
                                                    controls
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        borderRadius: 6,
                                                    }}
                                                />
                                            )}

                                            {/* delete image */}
                                            <IconButton
                                                size="small"
                                                style={{
                                                    position: "absolute",
                                                    top: 4,
                                                    right: 4,
                                                    backgroundColor: "#1F1F1F80",
                                                    color: "#fff",
                                                }}
                                                onClick={() => {
                                                    if (isLocal) {
                                                        setFiles((prev) => prev.filter((_, i) => i !== (idx - remoteMediaUrls.length)));
                                                        return;
                                                    }
                                                    setRemoteMediaUrls((prev) => prev.filter((_, i) => i !== idx));
                                                }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                                <Box id="upload-zone" className={classes.smallUploadBox}
                                    onDragOver={handleDragOver} onDrop={handleDrop}>
                                    <label htmlFor="welcome-upload" style={{ cursor: "pointer", margin: 0 }}>
                                        <Plus />
                                    </label>
                                    <input
                                        id="welcome-upload"
                                        type="file"
                                        hidden
                                        accept="image/*,video/mp4"
                                        multiple
                                        onChange={handleFileSelect}
                                    />
                                </Box>
                            </Box>
                            <Typography className={classes.infoText} style={{ margin: "8px 0 0" }}>
                                {previewItems.length}/{limit} pictures limit
                            </Typography>
                        </Box>
                        :
                        <Box id="upload-zone" className={classes.uploadBox}
                            onDragOver={handleDragOver} onDrop={handleDrop}>
                            <Box>
                                <label
                                    htmlFor={"welcome-upload"}
                                    style={{
                                        color: "#FE5A97",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                    }}
                                >
                                    Click to upload media
                                </label>

                                <input
                                    id="welcome-upload"
                                    type="file"
                                    hidden
                                    accept="image/*,video/mp4"
                                    multiple
                                    onChange={handleFileSelect}
                                />

                            </Box>
                            or drag and drop SVG, PNG, GIF, MP4
                            <br />
                            (max. 800x400 px)
                        </Box>

                    }

                    <Box mt={2} display="flex" style={{ gap: 8, marginTop: 32 }}>
                        <Checkbox
                            checked={isAgreementChecked}
                            onChange={(e) => setIsAgreementChecked(e.target.checked)}
                            icon={<IconCheckbox />}
                            checkedIcon={<IconCheckFill />}
                            indeterminateIcon={<IconCheckbox />}
                        />
                        <Typography style={{ lineHeight: "110%" }}>I confirm that all persons depicted are 18+ and I agree to the
                            <Link to="/content-provider-agreement"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#FE5A97", marginLeft: 8, textDecoration: "underline" }}
                            >
                                Content Provider Agreement
                            </Link>
                        </Typography>
                    </Box>
                    {(previewItems.length > 0 || initialMediaUrls.length > 0) && <Box style={{ display: "flex", justifyContent: "flex-end", gap: 20, marginTop: 24 }}>
                        <Button className={clsx(classes.cancelButton, classes.wideButton)} onClick={clearFiles}>
                            Cancel
                        </Button>
                        <Button
                            className={clsx(classes.saveButton, classes.wideButton)}
                            onClick={handleSave}
                            disabled={!isAgreementChecked}
                            style={{ opacity: isAgreementChecked ? 1 : 0.65 }}
                        >
                            Continue
                        </Button>
                    </Box>}
                </DialogContent>
            </Dialog>
        </Box >
    );
}
